/**
 * The ONE fan-out point for product analytics. Instrument features with `logAnalyticsEvent` and
 * the event lands in every sink that is currently live:
 *
 *   1. Firebase Analytics (GA4) — always on, config is in the repo, shared with the Spaces app.
 *   2. Segment → Amplitude + DWH — dormant until `VITE_SEGMENT_WRITE_KEY` exists.
 *   3. Amplitude DIRECT — live once `VITE_AMPLITUDE_API_KEY` exists. Deliberate deviation from
 *      the Segment-only architecture; see the header of `amplitude.ts` for what it trades away.
 *      Runs alongside sink 2 rather than replacing it, so finishing the Segment leg later is an
 *      addition. If BOTH are ever live and pointed at project 785010, events arrive twice —
 *      retire this one at that point.
 *   4. The first-party usage sink → our proxy → Firestore → the Usage board in this app.
 *
 * Each sink decides for itself whether it may send, so adding or retiring one is a change here
 * and nowhere else. Failures are swallowed in all directions: analytics must never be the reason
 * a page breaks.
 *
 * The PII rule (no candidate data in event names or properties — see `segment.ts`) applies to
 * every call site, because every call site now reaches three sinks.
 */
import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase';
import { trackEvent, trackClientError, trackPage, identifyUser } from './segment';
import { trackAmplitudeEvent, trackAmplitudePage, identifyAmplitudeUser } from './amplitude';
import { trackUsageEvent, trackUsagePage } from './usageSink';

type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;

/**
 * Firebase Analytics has no per-environment kill switch of its own (its config is compiled in),
 * so local runs are filtered here. Segment and the usage sink carry their own guards — including
 * their own debug opt-ins — which is why this check wraps ONLY the Firebase call.
 */
function isLocalhost() {
  if (typeof window === 'undefined') return false;
  const { hostname } = window.location;
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

/**
 * The ONLY error categories that may travel. `error.name` is a WRITABLE property — a thrown object
 * can carry any string there, including something copied off the screen — so it is matched against
 * this closed list rather than forwarded. Anything unrecognised collapses to `error`, which keeps
 * failures groupable without letting an arbitrary string reach three sinks.
 */
const ERROR_KINDS = new Set([
  'Error',
  'EvalError',
  'RangeError',
  'ReferenceError',
  'SyntaxError',
  'TypeError',
  'URIError',
  'AggregateError',
  'DOMException',
  'AbortError',
]);

/** Structured, PII-free category for whatever was thrown. Never the thrown value's own text. */
export function errorKind(error: unknown): string {
  if (error === null || error === undefined) return 'unknown';
  if (typeof error !== 'object') return typeof error;
  const name = (error as { name?: unknown }).name;
  if (typeof name === 'string' && ERROR_KINDS.has(name)) return name;
  return 'error';
}

/** Run a sink, absorbing anything it throws. */
function safely(send: () => void): void {
  try {
    send();
  } catch {
    // Analytics failures are non-fatal.
  }
}

export async function logAnalyticsEvent(
  eventName: string,
  params: AnalyticsParams = {}
): Promise<void> {
  safely(() => trackEvent(eventName, params));
  safely(() => trackAmplitudeEvent(eventName, params));
  safely(() => trackUsageEvent(eventName, params));
  if (isLocalhost()) return;
  try {
    const instance = await analytics;
    if (!instance) return;
    logEvent(instance, eventName, params);
  } catch {
    // Analytics failures are non-fatal
  }
}

/**
 * Identity, fanned out the same way. Callers: `AuthContext` on sign-in and sign-out.
 *
 * Goes through this module rather than calling a sink directly, for the same reason events do:
 * a sink added here must not need a second edit elsewhere to learn who the user is. Pass null on
 * sign-out — a shared browser must not keep attributing to the previous recruiter.
 */
export function logAnalyticsIdentify(email: string | null): void {
  safely(() => identifyUser(email));
  safely(() => identifyAmplitudeUser(email));
}

/** SPA route change, fanned out the same way. Callers: `AnalyticsPageTracker` in App.tsx. */
export function logAnalyticsPage(path: string): void {
  safely(() => trackPage(path));
  safely(() => trackAmplitudePage(path));
  safely(() => trackUsagePage(path));
}

/**
 * One global net for uncaught errors and unhandled rejections — the "where do people get stuck"
 * signal, and the reason the Usage board can show failures next to usage.
 *
 * It lives HERE rather than in `segment.ts` so it survives each sink's availability: the listeners
 * are installed once and every sink then applies its own gate. Only STRUCTURED metadata travels
 * (`source`, `kind`, `path`) — never the message text, which can quote anything that was on screen.
 */
/**
 * Report one caught failure. Also the entry point for code that catches an error itself and wants
 * it counted — call this rather than a sink directly, so the categorisation cannot be skipped.
 *
 * The value is categorised ONCE, here, and handed to every sink already sanitised: no sink sees
 * the raw error, so none of them can reintroduce the free-text `name`.
 */
export function logAnalyticsError(source: string, error: unknown): void {
  const kind = errorKind(error);
  safely(() => trackClientError(source, kind));
  safely(() => trackAmplitudeEvent('client_error', { source, kind }));
  safely(() => trackUsageEvent('client_error', { source, kind }));
}

let errorNetInstalled = false;
export function installErrorTracking(): void {
  if (errorNetInstalled || typeof window === 'undefined') return;
  errorNetInstalled = true;
  window.addEventListener('error', (event) =>
    logAnalyticsError('window.onerror', event.error ?? event.message)
  );
  window.addEventListener('unhandledrejection', (event) =>
    logAnalyticsError('unhandledrejection', event.reason)
  );
}

/** @internal test seam */
export function _resetErrorTrackingForTests(): void {
  errorNetInstalled = false;
}
